import { isEmpty } from "lodash";

import { UserContextType, RoleType } from "app/contexts/UserContext";
import {
  isFacilityAdminOrCoachAdmin,
  isFacilityStaff,
  showForNonFacilityAdminStaff,
  showForSchedulingAdminsOrCoaches,
} from "app/utils/roleAccess";

import Calendar from "./routes/Calendar";
import FacilityCalendarPage from "./routes/FacilityCalendarPage";
import FeedTimeline from "./routes/Feed/FeedTimeline";
import Groups from "./routes/Groups";
import Spaces from "./routes/Spaces";
import CreateNewSpace from "./routes/Spaces/CreateNewSpace";
import CreateNewMultipleSpaces from "./routes/Spaces/CreateNewMultipleSpaces";
import Tags from "./routes/Tags";
import AddTag from "./routes/Tags/AddTag";
import EditTag from "./routes/Tags/EditTag";
import DeleteTag from "./routes/Tags/DeleteTag";
import NotificationsEdit from "./routes/Account/NotificationsEdit";
import ProfileEdit from "./routes/Account/ProfileEdit";
import FamilyEdit from "./routes/Account/FamilyEdit";
import PasswordEdit from "./routes/Account/PasswordEdit";
import Benefits from "./routes/Benefits";
import CopyMovePost from "./components/CopyMovePost";
import GettingStarted from "./routes/GettingStarted";
import AcademySetup from "./routes/AcademySetup";
import UpgradePlus from "./routes/UpgradePlus";
import InternalServerErrorPage from "./routes/500";
import ForbiddenPage from "./routes/403";
import PageNotFound from "./routes/404";
import SpaceTemplate from "./routes/SpaceTemplates";
import BillingInfo from "./routes/Account/BillingInfo";
import BillingInfoEdit from "./routes/Account/BillingInfoEdit";
import Schedule from "./routes/Schedule";
import Subscriptions from "./routes/Account/Subscriptions";
import Sessions from "./routes/Sessions";
import NewSession from "./routes/Sessions/NewSession";
import EditSession from "./routes/Sessions/EditSession";
import PaymentConfirmation from "./routes/Sessions/PaymentConfirmation";
import EditConfirmation from "./routes/Sessions/SessionConfirmation/EditConfirmation";
import Availability from "./routes/Scheduling/Availability";
import Pricing from "./routes/Scheduling/Pricing";
import CalendarSettings from "./routes/Scheduling/CalendarSettings";
import CalendarDisplaySettings from "./routes/Scheduling/CalendarDisplaySettings";
import Settings from "./routes/Scheduling/Settings";
import SubscribeCalendar from "./routes/Scheduling/SubscribeCalendar";
import ActivityNotifications from "./routes/Activity/ActivityNotifications";
import ActivityInvitations from "./routes/Activity/ActivityInvitations";
import { Search } from "./routes/Search";
import Post from "./routes/Post";
import PostToSmartList from "./routes/PostToSmartList";
import DemoSpacesRouter from "./routes/DemoSpace/DemoSpaceTimeline/DemoSpacesRouter";
import CoachProfileEdit from "./routes/Account/CoachProfileEdit";
import Billing from "./routes/Billing";
import NewOffering from "./routes/Billing/NewOffering";
import ManagePrograms from "./routes/ManagePrograms";
import BuildProgram from "./routes/BuildProgram";
import ViewProgram from "./routes/ViewProgram";
import SinglePayment from "./routes/Billing/NewOffering/SinglePayment";
import Subscription from "./routes/Billing/NewOffering/Subscription";
import Setup from "./routes/Billing/Setup";
import AthleteHome from "./routes/AthleteHome";
import CoachProfileInfo from "./routes/CoachProfileInfo";
import Facility from "app/routes/Facility";
import FacilityUsers from "app/routes/FacilityUsers";
import ReportsPage from "./routes/Reporting";
import ReportPage from "./routes/Reporting/ReportPage";
import CustomerDetails from "./routes/Billing/Customers/CustomerDetails";
import AthletesSubscriptions from "./routes/Account/AthletesSubscriptions";
import ActivityBookingLogs from "./routes/Activity/ActivityBookingLogs";
import Contacts from "./routes/Contacts";
import CoachDetail from "./routes/Sessions/CoachDetail";
import SessionAvailability from "./routes/Sessions/SessionAvailability";
import PurchaseCheckout from "./routes/Checkout";
import GroupRouter from "./routes/Groups/GroupRouter";
import SpacesRouter from "./routes/Spaces/SpacesRouter";
import SegmentsRouter from "./routes/Segments/SegmentsRouter";
import GroupSettings from "./routes/Groups/GroupSettings";
import GroupPermissions from "./routes/Groups/GroupPermissions";
import TemplateRouter from "./routes/SpaceTemplates/TemplateTimeline/TemplateRouter";
import ResourcesRouter from "./routes/Resources/ResourcesRouter";
import LandingPage from "./routes/LandingPage";
import BookingOptions from "./routes/Facility/BookingOptions";
import Holidays from "./routes/Facility/Holidays";
import FacilityResources from "./routes/Facility/Resources";
import FacilityBilling from "./routes/Billing/FacilityBilling";
import PostLoginHandler from "./routes/PostLoginHandler";
import AutomationPage from "./routes/Automation";
import WorkflowDetailPage from "./routes/Automation/WorkflowDetail";

export const routerData = [
  {
    path: "/",
    exact: true,
    main: FeedTimeline,
    redirectTo: (user: UserContextType) => {
      if (user.role === RoleType.ADMIN) {
        return "/contacts";
      }

      if (user.role === RoleType.SCHEDULER) {
        return "/calendar/facility";
      }

      if (user.role === RoleType.COACH) {
        return "/spaces";
      }

      if (!isEmpty(user)) {
        return "/home";
      }

      return "/";
    },
  },
  {
    path: "/feed",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: FeedTimeline,
  },

  {
    path: "/calendar/facility",
    exact: true,
    main: FacilityCalendarPage,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar",
    exact: true,
    main: Calendar,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/multi-coach",
    exact: true,
    main: () => null,
    redirectTo: () => "/calendar/facility",
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/facility_calendar",
    exact: true,
    main: () => null,
    redirectTo: () => "/calendar/facility",
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions/payment_confirmation",
    exact: true,
    main: PaymentConfirmation,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions/edit/confirmation",
    exact: true,
    main: EditConfirmation,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions/edit",
    exact: true,
    main: EditSession,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions/session_availability",
    exact: true,
    main: SessionAvailability,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    // CN-2652: a coach's page, reached from the booking page's coach cards. It
    // belongs in this table rather than beside the public routes, because that
    // is what wraps a screen in the app shell.
    //
    // The page is `CoachDetail`, the app-native one: cards laid out for this
    // frame (43592:26426) rather than the public profile's list, and its own
    // four tabs. `CoachLanding` renders the public landing page's components
    // inside the shell instead — switching this one line back is all it takes to
    // return to it.
    // One pattern rather than four paths: this table's entries are also fed to
    // `Redirect from`, which takes a single string.
    path: "/sessions/coach/:coachExternalId/:tab(private|packages|events)?",
    exact: true,
    main: CoachDetail,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions/new",
    exact: true,
    main: NewSession,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/sessions",
    exact: true,
    main: Sessions,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/availability",
    exact: true,
    main: Availability,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/spaces",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Spaces,
  },
  {
    path: "/spaces/all/:listId/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Spaces,
  },
  {
    path: "/spaces/smartList/:listId/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Spaces,
  },
  {
    path: "/spaces/list/:listId/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Spaces,
  },
  {
    path: "/spaces/smartList/:listId/post_to_smart_list/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: PostToSmartList,
  },
  {
    path: "/spaces/new",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: CreateNewSpace,
  },
  {
    path: "/spaces/new/multiple",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: CreateNewMultipleSpaces,
  },
  {
    path: "/spaces/demo/:id",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: DemoSpacesRouter,
  },
  {
    path: "/spaces/:id",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: SpacesRouter,
  },
  {
    path: "/programs",
    exact: true,
    main: ManagePrograms,
  },
  {
    path: "/programs/build",
    exact: true,
    main: BuildProgram,
  },
  {
    path: "/programs/:program_id/view",
    exact: true,
    main: ViewProgram,
  },
  {
    path: "/purchase/:id",
    exact: true,
    main: PurchaseCheckout,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/search",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Search,
  },
  {
    path: "/space_templates",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: SpaceTemplate,
  },
  {
    path: "/space_templates/:id",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: TemplateRouter,
  },

  {
    path: "/schedule",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Schedule,
  },

  {
    path: "/resources",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: ResourcesRouter,
  },

  {
    path: "/groups",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Groups,
  },
  {
    path: "/groups/:id/settings/general",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: GroupSettings,
  },
  {
    path: "/groups/:id/settings/permissions",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: GroupPermissions,
  },
  {
    path: "/groups/:id",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: GroupRouter,
  },
  {
    path: "/lists",
    access: showForNonFacilityAdminStaff,
    exact: false,
    main: SegmentsRouter,
  },

  {
    path: "/activity/notifications",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: ActivityNotifications,
  },
  {
    path: "/activity/invitations",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: ActivityInvitations,
  },
  {
    path: "/activity/booking_logs",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: ActivityBookingLogs,
  },

  {
    path: "/account/settings",
    exact: true,
    main: NotificationsEdit,
  },
  // NOTE: private route only for coaches
  {
    path: "/account/coach-profile",
    exact: true,
    main: CoachProfileEdit,
  },
  {
    path: "/account/edit",
    exact: true,
    main: ProfileEdit,
  },
  {
    path: "/account/family",
    exact: true,
    main: FamilyEdit,
  },

  {
    path: "/account/tags",
    exact: true,
    main: Tags,
  },
  {
    path: "/account/tags/new",
    exact: true,
    main: AddTag,
  },
  {
    path: "/account/tags/:id/edit",
    exact: true,
    main: EditTag,
  },
  {
    path: "/account/tags/:id/delete",
    exact: true,
    main: DeleteTag,
  },

  {
    path: "/account/paid_subscriptions",
    exact: true,
    redirectTo: () => "/account/subscriptions",
    main: Subscriptions,
  },
  {
    path: "/account/subscriptions",
    exact: true,
    main: Subscriptions,
  },
  {
    path: "/account/billing_info",
    exact: true,
    main: BillingInfo,
  },
  {
    path: "/account/billing_info/edit",
    exact: true,
    main: BillingInfoEdit,
  },
  {
    path: "/account/password",
    exact: true,
    main: PasswordEdit,
  },
  {
    path: "/account/athlete_subscriptions",
    exact: true,
    main: AthletesSubscriptions,
  },
  {
    path: "/account/benefits",
    exact: true,
    main: Benefits,
  },
  {
    path: "/automation/facility-workflows/workflow/:id",
    exact: true,
    main: WorkflowDetailPage,
  },
  {
    path: "/automation/facility-workflows",
    exact: true,
    main: AutomationPage,
  },
  {
    path: "/automation",
    exact: true,
    main: AutomationPage,
    redirectTo: () => "/automation/facility-workflows",
  },

  {
    path: "/copy/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: CopyMovePost,
  },
  {
    path: "/move/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: CopyMovePost,
  },
  {
    path: "/getting-started",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: GettingStarted,
  },
  {
    path: "/academy/setup",
    exact: true,
    main: AcademySetup,
  },
  {
    path: "/upgrade-plus",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: UpgradePlus,
  },
  {
    path: "/posts/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Post,
  },
  {
    path: "/home",
    exact: true,
    main: AthleteHome,
  },
  {
    path: "/home/:coachId",
    exact: true,
    main: CoachProfileInfo,
  },

  {
    path: "/500",
    exact: true,
    main: InternalServerErrorPage,
  },
  {
    path: "/403",
    exact: true,
    main: ForbiddenPage,
  },
  {
    path: "/calendar/pricing",
    exact: true,
    main: Pricing,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/scheduling",
    exact: true,
    main: Settings,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/settings",
    exact: true,
    main: CalendarSettings,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/display",
    exact: true,
    main: CalendarDisplaySettings,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/calendar/integration",
    exact: true,
    main: SubscribeCalendar,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/contacts",
    exact: true,
    main: Contacts,
  },
  {
    path: "/billing",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Billing,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/offerings/create",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: NewOffering,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/payment/create",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: SinglePayment,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/subscription/create",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Subscription,
    permission: "scheduling",
    permissionObject: "feature",
  },

  {
    path: "/stripe/setup",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: Setup,
    permission: "scheduling",
    permissionObject: "feature",
  },

  {
    path: "/manage_facility",
    exact: true,
    main: Facility,
    access: isFacilityAdminOrCoachAdmin,
  },
  {
    path: "/manage_facility/users",
    exact: true,
    main: FacilityUsers,
    access: isFacilityAdminOrCoachAdmin,
  },
  {
    path: "/manage_facility/landing_page",
    exact: true,
    main: LandingPage,
    access: isFacilityAdminOrCoachAdmin,
  },
  {
    path: "/manage_facility/booking_options",
    exact: true,
    main: BookingOptions,
    access: isFacilityAdminOrCoachAdmin,
  },
  // One page on two paths. Admins and Coach Admins edit on the first path,
  // coaches and schedulers read on the second. The page sends each user to
  // the path of their mode.
  {
    path: "/manage_facility/resources",
    exact: true,
    main: FacilityResources,
    access: isFacilityStaff,
  },
  {
    path: "/facility_resources",
    exact: true,
    main: FacilityResources,
    access: isFacilityStaff,
  },
  {
    path: "/manage_facility/holidays",
    exact: true,
    main: Holidays,
    access: isFacilityAdminOrCoachAdmin,
  },
  {
    path: "/facility_billing",
    exact: true,
    main: FacilityBilling,
    access: isFacilityAdminOrCoachAdmin,
  },
  {
    path: "/facility_billing/:id",
    exact: true,
    main: CustomerDetails,
    access: isFacilityAdminOrCoachAdmin,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/reports",
    access: showForSchedulingAdminsOrCoaches,
    exact: true,
    main: ReportsPage,
  },
  {
    path: "/reports/:reportId",
    access: showForSchedulingAdminsOrCoaches,
    exact: true,
    main: ReportPage,
  },
  {
    path: "/billing/:id",
    access: showForNonFacilityAdminStaff,
    exact: true,
    main: CustomerDetails,
    permission: "scheduling",
    permissionObject: "feature",
  },
  {
    path: "/post-login",
    exact: true,
    main: PostLoginHandler,
  },
  // NOTE: dont add components below this line
  {
    path: "*",
    exact: true,
    main: PageNotFound,
  },
];
